// AdminHistory.tsx
import React, { useState } from 'react';
import SidebarAdmin from './SidebarAdmin';
import AdminNavbar from './AdminNavbar';
import PostAdminButton from '../Assets/PostAdminButton';
import ModalConfirmation from '../Assets/ModalConfirmation';
import AdminSearchBar from '../Assets/AdminSearchBar';
import HistoryModal from './HistoryModal';
import { HistoryAbout } from '../Types/Types';
import AdminHistoryTable from './AdminHistoryTable';
import useHistory from '../Hooks/useHistory';
import HistoryEditModal from './HistoryEditModal';
import { useTranslation } from 'react-i18next';

interface AdminHistoryProps {
    lang: string;
}

const AdminHistory: React.FC<AdminHistoryProps> = ({ lang }) => {
    const { t } = useTranslation()
    const [modalOpen, setModalOpen] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
    const [historyToEdit, setHistoryToEdit] = useState<HistoryAbout | null>(null);


    const {
        history,
        loading,
        error,
        createHistory,
        searchQuery,
        setSearchQuery,
        setSortBy,
        setSortOrder,
        updateHistory,
        deleteHistory,
        reorderHistory
    } = useHistory();


    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const handleDeleteRequest = (id: string) => {
        setSelectedHistoryId(id);
        setModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedHistoryId) {
            await deleteHistory(selectedHistoryId);
            setSelectedHistoryId(null);
        }
        setModalOpen(false);
    };

    const handleDeleteCancel = () => {
        setSelectedHistoryId(null);
        setModalOpen(false);
    };


    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <div className="w-64">
                <SidebarAdmin />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                <AdminNavbar />
                <div className="flex-1 py-12 px-48 space-y-12">
                    <div className="flex justify-between items-center">
                        <h1 className="text-4xl text-blue-900">
                            {t(`navbar.history`)}
                        </h1>

                    </div>

                    <div className="flex justify-between items-center space-x-4">
                        <PostAdminButton onClick={() => setShowForm(true)} />
                        <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
                    </div>

                    <AdminHistoryTable
                        history={history}
                        loading={loading}
                        error={error}
                        onDelete={handleDeleteRequest}
                        onEdit={(historyData) => {
                            setHistoryToEdit(historyData);
                            setShowEditForm(true);
                        }}
                        onSortChange={(field, order) => {
                            setSortBy(field);
                            if (order === 'asc' || order === 'desc') {
                                setSortOrder(order);
                            }
                        }}
                        onReorder={async (newOrder: HistoryAbout[]) => {
                            // Map the array to include the position index
                            const reorderedWithPosition = newOrder.map((item, index) => ({
                                _id: item._id,
                                position: index + 1,  // or just index if your backend expects 0-based
                            }));

                            await reorderHistory(reorderedWithPosition);
                        }}
                        lang={lang}
                    />
                </div>
            </div>

            {/* Create Modal */}
            <HistoryModal
                isOpen={showForm}
                onClose={() => setShowForm(false)}
                onSubmit={async (historyData) => {
                    await createHistory(historyData.text);
                    setShowForm(false);
                }}

            />
            {showEditForm && historyToEdit && (
                <HistoryEditModal
                    isOpen={showEditForm}
                    onClose={() => {
                        setShowEditForm(false);
                        setHistoryToEdit(null);
                    }}
                    onSubmit={async (id, formData) => {
                        await updateHistory(id, formData);
                        setShowEditForm(false);
                        setHistoryToEdit(null);
                    }}
                    initialData={historyToEdit}
                    lang={lang}
                />
            )}




            {/* Delete Confirmation Modal */}
            <ModalConfirmation
                isOpen={modalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export default AdminHistory;